import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Sparkles, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const INTEREST_OPTIONS = [
  { key: "OPEN_GYM", label: "Open Gym" },
  { key: "STRENGTH", label: "Strength & Conditioning" },
  { key: "MMA", label: "MMA" },
  { key: "HYROX", label: "Hyrox" },
  { key: "PT", label: "Personal Training" },
  { key: "GENERAL", label: "Not sure — just curious" },
];

export default function PublicReferralPage() {
  const { referrerId } = useParams();
  const [referrer, setReferrer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "GENERAL", message: "" });

  React.useEffect(() => {
    axios.get(`${API}/api/public/referrers/${referrerId}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setNotFound(true));
  }, [referrerId]);

  const submit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Name & phone are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/referrals`, { referrer_id: referrerId, ...form });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white" data-testid="public-referral-notfound">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-4xl uppercase tracking-tight font-black">Link not found</h1>
          <p className="text-zinc-500">This referral link is invalid or expired. Visit <Link to="/" className="text-volt underline">nammaxfit.com</Link> to sign up directly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="public-referral-page">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">NammaXFit</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-2">Referred by a Friend</h1>
        </div>

        {!submitted && (
          <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-6 space-y-4" data-testid="public-referral-form">
            {referrer && (
              <div className="bg-volt/10 border border-volt/30 rounded-sm p-3 flex items-center gap-2 text-sm" data-testid="referrer-hero">
                <Gift size={16} className="text-volt shrink-0" />
                <div>
                  <span className="font-semibold text-volt">{referrer.name}</span>
                  <span className="text-zinc-400"> invited you to </span>
                  <span className="font-semibold">{referrer.branch_name || "NammaXFit"}</span>
                </div>
              </div>
            )}

            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
              <Sparkles size={12} className="text-volt" />
              Drop your details — we'll reach out with a starter plan.
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Your name *</Label>
              <Input data-testid="public-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-black border-zinc-800 mt-1 rounded-sm" placeholder="Priya S." />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Phone *</Label>
              <Input data-testid="public-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-black border-zinc-800 mt-1 rounded-sm" placeholder="+91 9..." />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Email</Label>
              <Input data-testid="public-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-zinc-800 mt-1 rounded-sm" placeholder="you@..." />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Interested in</Label>
              <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                <SelectTrigger className="bg-black border-zinc-800 mt-1 rounded-sm" data-testid="public-interest">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTEREST_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Anything else? (optional)</Label>
              <Textarea data-testid="public-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={2} className="bg-black border-zinc-800 mt-1 rounded-sm" placeholder="Any goals, timing, etc." />
            </div>

            <Button
              onClick={submit}
              disabled={busy}
              data-testid="public-submit"
              className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm h-11"
            >
              <Send size={14} className="mr-2" />
              {busy ? "Sending…" : "Send My Details"}
            </Button>

            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-600 text-center pt-2">
              By submitting, you agree to be contacted by the NammaXFit team.
            </div>
          </div>
        )}

        {submitted && (
          <div className="bg-zinc-950 border border-volt/40 rounded-sm p-8 text-center space-y-4" data-testid="public-referral-success">
            <CheckCircle2 size={48} className="text-volt mx-auto" />
            <h2 className="font-display text-3xl font-black uppercase tracking-tight">You're in.</h2>
            <p className="text-zinc-400">
              Our team will call you within 24 hours to line up your trial at{" "}
              <span className="text-volt font-semibold">{referrer?.branch_name || "your nearest branch"}</span>.
            </p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-600 pt-2">
              Shared by {referrer?.name || "a friend"}
            </p>
          </div>
        )}

        <div className="mt-6 text-center text-[10px] uppercase tracking-[0.22em] text-zinc-700">
          NammaXFit · Bengaluru's Strength Lab
        </div>
      </div>
    </div>
  );
}
